import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import JournalEntryService, { JournalEntry, JournalLine } from '../../services/journal-entry.service';
import { NumberTextField } from '../../components/common';
import AccountService from '../../services/account.service';
import EmployeeService from '../../services/employee.service';

interface LineItem {
  tempId: string;
  id?: number;
  account: any;
  debit: number;
  credit: number;
  description: string;
  lineNumber: number;
}

const JournalEntryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  
  const [lines, setLines] = useState<LineItem[]>([]);

  const [formData, setFormData] = useState<JournalEntry>({
    entryNumber: `JE-${Date.now()}`,
    date: new Date().toISOString().split('T')[0],
    description: '',
    createdBy: 0,
    status: 'draft',
    reference: '',
    totalDebit: 0,
    totalCredit: 0,
  });

  useEffect(() => {
    fetchAccounts();
    fetchEmployees();
    if (isEdit && id) {
      fetchJournalEntry(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await AccountService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
        sort: 'accountNumber:asc',
      });
      setAccounts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await EmployeeService.getAll({
        'filters[status][$eq]': 'active',
        'pagination[pageSize]': 100,
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchJournalEntry = async (entryId: number) => {
    try {
      setLoading(true);
      const response = await JournalEntryService.getOne(entryId);
      const entryData = response.data?.data;

      if (entryData) {
        const attrs = entryData.attributes;
        setFormData({
          entryNumber: attrs.entryNumber,
          date: attrs.date,
          description: attrs.description,
          createdBy: attrs.createdBy?.data?.id || 0,
          status: attrs.status || 'draft',
          reference: attrs.reference || '',
          totalDebit: attrs.totalDebit || 0,
          totalCredit: attrs.totalCredit || 0,
        });

        if (attrs.createdBy?.data) {
          setSelectedEmployee(attrs.createdBy.data);
        }

        const linesResponse = await JournalEntryService.getLines(entryId);
        const linesData = linesResponse.data?.data || [];
        
        const mappedLines: LineItem[] = linesData.map((line: any) => ({
          tempId: `line-${line.id}`,
          id: line.id,
          account: line.attributes?.account?.data,
          debit: line.attributes?.debit || 0,
          credit: line.attributes?.credit || 0,
          description: line.attributes?.description || '',
          lineNumber: line.attributes?.lineNumber || 0,
        }));
        
        setLines(mappedLines);
      }
    } catch (error) {
      console.error('Error fetching journal entry:', error);
      setError('Failed to load journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addLine = () => {
    setLines([...lines, {
      tempId: `temp-${Date.now()}`,
      account: null,
      debit: 0,
      credit: 0,
      description: '',
      lineNumber: lines.length + 1,
    }]);
  };

  const removeLine = (tempId: string) => {
    setLines(lines.filter((line) => line.tempId !== tempId));
  };

  const updateLine = (tempId: string, field: keyof LineItem, value: any) => {
    setLines(
      lines.map((line) => {
        if (line.tempId === tempId) {
          const updated = { ...line, [field]: value };
          if (field === 'debit' || field === 'credit') {
            updated[field] = Number(value) || 0;
          }
          return updated;
        }
        return line;
      })
    );
  };

  // Calculate totals
  useEffect(() => {
    const totalDebit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);

    setFormData((prev) => ({
      ...prev,
      totalDebit: Number(totalDebit) || 0,
      totalCredit: Number(totalCredit) || 0,
    }));
  }, [lines]);

  const validateForm = (): boolean => {
    if (!formData.createdBy) {
      setError('Please select the employee creating this entry');
      return false;
    }
    if (lines.length === 0) {
      setError('Please add at least one journal line');
      return false;
    }
    if (lines.some(line => !line.account)) {
      setError('All lines must have an account selected');
      return false;
    }
    if (lines.every(line => line.debit === 0 && line.credit === 0)) {
      setError('At least one line must have a debit or credit amount');
      return false;
    }
    const totalDebit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      setError('Total debits must equal total credits. Journal entry is not balanced.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let entryId: number;

      if (isEdit && id) {
        await JournalEntryService.update(parseInt(id), formData);
        entryId = parseInt(id);
        setSuccess('Journal Entry updated!');
      } else {
        const response = await JournalEntryService.create(formData);
        entryId = response.data?.data?.id;
        setSuccess('Journal Entry created!');
      }

      if (entryId) {
        if (isEdit) {
          const oldLinesResponse = await JournalEntryService.getLines(entryId);
          const oldLines = oldLinesResponse.data?.data || [];
          await Promise.all(
            oldLines.map((line: any) => JournalEntryService.deleteLine(line.id))
          );
        }

        await Promise.all(
          lines.map((line, index) =>
            JournalEntryService.createLine({
              journalEntry: entryId,
              account: line.account.id,
              debit: line.debit,
              credit: line.credit,
              description: line.description,
              lineNumber: index + 1,
            })
          )
        );
      }

      setTimeout(() => {
        navigate('/journal-entries');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving journal entry:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save journal entry. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isBalanced = Math.abs((formData.totalDebit || 0) - (formData.totalCredit || 0)) < 0.01;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Journal Entry' : 'New Journal Entry'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {!isBalanced && lines.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Entry is not balanced! Debits: {(formData.totalDebit || 0).toFixed(2)}, Credits: {(formData.totalCredit || 0).toFixed(2)}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Header Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Entry Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Entry Number"
                  name="entryNumber"
                  value={formData.entryNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                />
                <TextField
                  fullWidth
                  required
                  label="Date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                fullWidth
                required
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Reference"
                  name="reference"
                  value={formData.reference}
                  onChange={handleChange}
                  helperText="Invoice #, Receipt #, etc."
                />
                <Autocomplete
                  fullWidth
                  value={selectedEmployee}
                  onChange={(e, v) => {
                    setSelectedEmployee(v);
                    setFormData({ ...formData, createdBy: v?.id || 0 });
                  }}
                  options={employees}
                  loading={employeesLoading}
                  getOptionLabel={(o) => `${o.attributes?.firstName} ${o.attributes?.lastName}`}
                  renderInput={(params) => <TextField {...params} label="Created By *" required />}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Journal Lines */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Journal Lines</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addLine}
                size="small"
              >
                Add Line
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="35%">Account</TableCell>
                    <TableCell width="25%">Description</TableCell>
                    <TableCell width="15%">Debit</TableCell>
                    <TableCell width="15%">Credit</TableCell>
                    <TableCell width="10%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No lines added. Click "Add Line" to start.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lines.map((line) => (
                      <TableRow key={line.tempId}>
                        <TableCell>
                          <Autocomplete
                            value={line.account}
                            onChange={(e, v) => updateLine(line.tempId, 'account', v)}
                            options={accounts}
                            loading={accountsLoading}
                            getOptionLabel={(o) => `${o.attributes?.accountNumber} - ${o.attributes?.accountName}`}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Select account" required />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={line.description}
                            onChange={(e) => updateLine(line.tempId, 'description', e.target.value)}
                            placeholder="Line description"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={line.debit}
                            onChange={(e) => updateLine(line.tempId, 'debit', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">OMR</InputAdornment>,
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={line.credit}
                            onChange={(e) => updateLine(line.tempId, 'credit', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">OMR</InputAdornment>,
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeLine(line.tempId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Totals Summary */}
          <Card variant="outlined" sx={{ bgcolor: isBalanced ? 'success.light' : 'error.light' }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color={isBalanced ? 'success.contrastText' : 'error.contrastText'}>
                    Total Debits:
                  </Typography>
                  <Typography variant="h6" color={isBalanced ? 'success.contrastText' : 'error.contrastText'}>
                    OMR {(formData.totalDebit || 0).toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color={isBalanced ? 'success.contrastText' : 'error.contrastText'}>
                    Total Credits:
                  </Typography>
                  <Typography variant="h6" color={isBalanced ? 'success.contrastText' : 'error.contrastText'}>
                    OMR {(formData.totalCredit || 0).toFixed(2)}
                  </Typography>
                </Stack>
                <Divider sx={{ borderColor: isBalanced ? 'success.contrastText' : 'error.contrastText' }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h5" color={isBalanced ? 'success.contrastText' : 'error.contrastText'} fontWeight="bold">
                    Difference:
                  </Typography>
                  <Typography variant="h4" color={isBalanced ? 'success.contrastText' : 'error.contrastText'} fontWeight="bold">
                    OMR {Math.abs((formData.totalDebit || 0) - (formData.totalCredit || 0)).toFixed(2)}
                  </Typography>
                </Stack>
                {isBalanced && (
                  <Typography variant="body2" color="success.contrastText" align="center" fontWeight="bold">
                    ✓ Entry is balanced
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/journal-entries')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || !isBalanced}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Entry' : 'Create Entry'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default JournalEntryForm;

